import { notification } from 'antd';
import type { ArgsProps as NotificationArgsProps } from 'antd/es/notification';

type TError = {
  message: string;
};

type TApiError = TError & {
  messages: string[];
  statusCode: number;
  isCancelled: boolean;
};

type TErrorHandlerOptions = {
  customDescription?: string | ((error: TError) => string);
  customTitle?: string;
  disableLogs?: boolean;
  duration?: number;
  notify?: boolean;
  onApiError?: (error: TApiError) => void;
  onCodeError?: (error: TError) => void;
  onError?: (error: TError | TApiError) => void;
  showProgress?: boolean;
};

export const isError = (error: unknown): error is TError =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

export const isApiError = (error: unknown): error is TApiError => {
  try {
    return (
      isError(error) &&
      'messages' in error &&
      Array.isArray((error as TApiError).messages) &&
      (error as TApiError).messages.every((m) => typeof m === 'string') &&
      'statusCode' in error &&
      ((error as TApiError).statusCode === undefined ||
        typeof (error as TApiError).statusCode === 'number') &&
      'isCancelled' in error &&
      typeof (error as TApiError).isCancelled === 'boolean'
    );
  } catch {
    return false;
  }
};

const formatMessages = (messages: string[]): string | undefined =>
  messages.length > 0 ? messages.map((m) => `\n- ${m}`).join('') : undefined;

const showNotification = (props: NotificationArgsProps) => {
  notification.error(props);
};

/**
 * Универсальный обработчик ошибок.
 * @param error - Ошибка.
 * @param options - Опции обработчика.
 */
export const handleError = (
  error: unknown,
  options: TErrorHandlerOptions = {}
): void => {
  if (!isError(error)) return;

  const {
    customDescription,
    customTitle,
    disableLogs = false,
    duration = 5,
    notify = true,
    onApiError,
    onCodeError,
    onError,
    showProgress = false
  } = options;

  const title = customTitle || error.message;
  let description: string | undefined;

  if (customDescription) {
    if (typeof customDescription === 'function') {
      description = customDescription(error);
    } else {
      description = customDescription;
    }
  }

  if (isApiError(error)) {
    if (error.isCancelled) return;

    description = description ?? formatMessages(error.messages);

    if (notify)
      showNotification({
        title: title || 'Произошла ошибка',
        description,
        showProgress,
        duration
      });

    if (!disableLogs) console.error('[API Error]', error);

    onApiError?.(error);
    onError?.(error);

    return;
  }

  if (notify)
    showNotification({
      title: title || 'Произошла ошибка',
      description,
      showProgress,
      duration
    });

  if (!disableLogs) {
    console.error(
      '%c[Code Error]',
      'color: white; padding: 2px 4px; background-color: #ff2e1f; border-radius: 4px;',
      error
    );
  }

  onCodeError?.(error);
  onError?.(error);
};
